// client/src/App.tsx
import { useEffect, useState } from "react";
import { Sprout } from "lucide-react";

import Header from "./components/Header.tsx";
import Footer from "./components/Footer.tsx";
import FarmerDashboard from "./pages/FarmerDashboard.tsx";
import AdminDashboard from "./pages/AdminDashboard.tsx";
import FarmDetail from "./pages/FarmDetail.tsx";
import SendNotification from "./pages/SendNotification.tsx";

import Admin from "./pages/Admin.tsx";
import PestControl from "./pages/PestControl.tsx";
import VoiceQuery from "./pages/VoiceQuery.tsx";
import ErrorBoundary from "./components/ErrorBoundary.tsx";

import { OfflineModeIndicator } from "./components/OfflineModeIndicator.tsx";


import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import { useOnlineStatus } from "./hooks/useOnlineStatus.ts";
import { Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext.tsx";
import { initOfflineStorage } from "./utils/offlineStorage.ts";
import { offlineManager } from "./utils/offlineManager";
// clearAuth disabled to preserve offline data


export default function App() {

  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { user, loading, isOfflineMode } = useAuth();
  const online = useOnlineStatus();
  
  const isAdmin = user?.email === 'kutloano.moshao111@gmail.com';

  useEffect(() => {
    // Initialize comprehensive offline system
    const initOfflineSystem = async () => {
      try {
        // Initialize legacy offline storage for compatibility
        initOfflineStorage();
        
        // Initialize advanced offline manager
        // offlineManager is already initialized as singleton
        
        // Setup offline event listeners
        offlineManager.on('online', () => {
          console.log('🌐 Back online - syncing data');
        });
        
        offlineManager.on('offline', () => {
          console.log('📱 Offline mode activated');
        });
        
        offlineManager.on('sync-completed', (data) => {
          console.log('✅ Background sync completed:', data);
        });
        
        offlineManager.on('sync-failed', (error) => {
          console.warn('⚠️ Background sync failed:', error);
        });
        
        // Preload data in background without blocking UI
        if (navigator.onLine) {
          setTimeout(async () => {
            try {
              const { apiClient } = await import('./utils/apiClient.ts');
              const serverReady = await apiClient.waitForServer(3000); // Reduced to 3 seconds
              
              if (serverReady) {
                offlineManager.preloadData([
                  '/api/weather-data?location=Maseru,Lesotho',
                  '/api/market-prices',
                  '/api/pest-weather-alerts'
                ]).catch(console.warn);
              }
            } catch (error) {
              console.warn('Background preload failed:', error);
            }
          }, 100); // Start after 100ms
        }
        
        console.log('🚀 Advanced offline system initialized');
      } catch (error) {
        console.error('Failed to initialize offline system:', error);
      }
    };
    
    initOfflineSystem();
  }, []);



  useEffect(() => {
    // Initialize push notifications for authenticated users
    if (user && online && !isOfflineMode) {
      // Check if user has push notifications enabled
      const initializePushNotifications = async () => {
        try {
          const { checkPushSubscription } = await import('./utils/notifications');
          const isSubscribed = await checkPushSubscription();
          
          if (!isSubscribed) {
            // Don't auto-subscribe, let user choose in settings
            // Push notifications available but not enabled
          }
        } catch (error) {
          console.error('Push notification initialization error:', error);
        }
      };
      
      initializePushNotifications();
    }
  }, [user, online, isOfflineMode]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-green-50">
        <div className="flex items-center justify-center p-4 bg-green-600 rounded-lg mb-4">
          <Sprout className="w-8 h-8 text-white animate-pulse" />
        </div>
        <h2 className="text-xl font-bold text-green-800 mb-2">AgroSense</h2>
        <p className="text-green-600">Loading your farming dashboard...</p>
        <div className="mt-4 w-32 h-1 bg-green-200 rounded-full overflow-hidden">
          <div className="h-full bg-green-600 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden">
      <Header online={online} />
      <main className="flex-1 w-full">
        <div className="w-full max-w-full">
          <Routes>
            <Route path="/" element={
              isAdmin ? 
                <AdminDashboard user={user} /> : 
                <FarmerDashboard online={online} user={user} isOfflineMode={isOfflineMode} />
            } />
            <Route path="/farms/:id" element={<FarmDetail user={user} />} />
            <Route path="/pest-control" element={<PestControl user={user} />} />
            <Route path="/voice-query" element={<VoiceQuery user={user} />} />
            <Route path="/admin/send-notification" element={<SendNotification />} />
            <Route path="/admin" element={<Admin />} />

          </Routes>
        </div>
      </main>
      <Footer />
      <OfflineModeIndicator />

    </div>
  );
}